import React, { useEffect, useRef, useState } from "react";
import {
    GetTechnologicalMapsQuery,
    useGetTechMapsForServiceQuery,
    useUpdateTechMapConnectionForServiceMutation,
} from "@me-team/host/main/technologicalMaps/graphql/technologicalMaps.hooks";
import { RefetchFunction } from "../types";
import { Button, Form } from "react-bootstrap";
import { Pagination, RenderEmptyList, Spinner, TooltipCustom } from "@services/ui-components";
import { useParams } from "react-router-dom";
import RenderCategoryName from "../RenderComponents/RenderCategoryName";
import { truncateText } from "../constants";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../../services/ErrorService";
import { CompanyAccess, TechnologicalMapProxy } from "@me-team/host/main/graphql/types";
import { ActionUpdateType } from "../../../utils/enums";
import UpdateRecoveryTechMap from "../UpdateRecoveryTechMap/UpdateRecoveryTechMap";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import useScrollToTopOnChange from "../../../hooks/useScrollToTopOnChange";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface TechMapsListForServiceTable {
    filters: {
        search: string;
        categoryId: number;
    };
    setRefetchTechMaps: (refetchTechMaps: RefetchFunction<GetTechnologicalMapsQuery>) => void;
    onShowCreateTechMapModal: () => void;
    toggleShowToast?: (text: string) => void;
}

const TechMapsListForServiceTable: React.FC<TechMapsListForServiceTable> = ({
    filters,
    onShowCreateTechMapModal,
    setRefetchTechMaps,
    toggleShowToast,
}) => {
    const { t } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canEditServiceOnlineBooking = useRoleAccess(CompanyAccess.ServiceAndOnlineBookingEdit);
    const canEditServiceTechMaps = useRoleAccess(CompanyAccess.ServiceTechMapsEdit);
    const canCreateTechMap = useRoleAccess(CompanyAccess.TechMapCreate);
    const canCreateTechMapCategory = useRoleAccess(CompanyAccess.TechMapCategoryCreate);
    const { serviceId, branchId } = useParams();
    const parentCheckboxRef = useRef<HTMLInputElement>(null);
    const [allChecked, setAllChecked] = useState<boolean>(false);
    const [indeterminate, setIndeterminate] = useState<boolean>(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [showUpdateRecoveryTechMapModal, setShowUpdateRecoveryTechMapModal] = useState(false);
    const [currentAction, setCurrentAction] = useState<ActionUpdateType | null>(null);
    const [currentUpdateTechMap, setCurrentUpdateTechMap] = useState(null);

    useScrollToTopOnChange(currentPage);

    const isFilterNotEmpty = Object.values(filters).some((value) => value !== "" && value !== null);

    const resetCurrentPage = () => {
        setCurrentPage(1);
    };
    useEffect(() => {
        resetCurrentPage();
    }, [filters?.search, filters?.categoryId]);

    const {
        data: techMapsData,
        loading: techMapsLoading,
        refetch: refetchTechMaps,
    } = useGetTechMapsForServiceQuery({
        variables: {
            page: currentPage,
            itemsPerPage: itemsPerPage,
            search: filters?.search ? filters?.search : null,
            categoryId: filters?.categoryId ? filters?.categoryId : null,
            connect: true,
            companyId: +currentCompanyId,
            serviceId: +serviceId,
        },
        skip: !currentCompanyId || !serviceId,
        fetchPolicy: "network-only",
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    useEffect(() => {
        if (setRefetchTechMaps) {
            setRefetchTechMaps(refetchTechMaps);
        }
    }, [refetchTechMaps, setRefetchTechMaps]);

    const [updateTechMapConnection] = useUpdateTechMapConnectionForServiceMutation({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const isTechMapConnected = (techMap: TechnologicalMapProxy) => {
        return techMap?.technologicalMapServiceRelations?.length > 0;
    };

    const company = techMapsData?.user?.company;
    const techMaps: TechnologicalMapProxy[] = company?.techMapListForServices?.technologicalMaps;
    const pageCount = parseInt(company?.techMapListForServices?.pageCount || "0");
    const totalItems = company?.techMapListForServices?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);

    useEffect(() => {
        if (techMaps && parentCheckboxRef.current) {
            const all = techMaps.every(isTechMapConnected);
            const some = techMaps.some(isTechMapConnected);
            setAllChecked(all);
            setIndeterminate(!all && some);
        }
    }, [techMaps]);

    useEffect(() => {
        if (parentCheckboxRef.current) {
            parentCheckboxRef.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);

    const handleParentCheckboxChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!canEditServiceOnlineBooking) return;

        const isChecked = e.target.checked;
        const techMapIds = techMaps?.map((service) => service.id);
        const connect = indeterminate ? false : isChecked;

        await updateTechMapConnection({
            variables: {
                serviceId: +serviceId,
                techMapIds,
                connect,
                branchId: Number(branchId),
            },
        });

        await refetchTechMaps();
        toggleShowToast(t("Saved"));
    };

    const handleTechMapCheckboxChange = async (techMap: TechnologicalMapProxy) => {
        if (!canEditServiceOnlineBooking) return;

        await updateTechMapConnection({
            variables: {
                serviceId: +serviceId,
                techMapIds: [techMap.id],
                connect: !isTechMapConnected(techMap),
                branchId: Number(branchId),
            },
        });

        await refetchTechMaps();
        toggleShowToast(t("Saved"));
    };

    const toggleUpdateTechMapModal = () => setShowUpdateRecoveryTechMapModal((prevState) => !prevState);

    const handleOpenEditTechMap = (action: ActionUpdateType, techMap: TechnologicalMapProxy) => {
        if (!canEditServiceTechMaps) return;

        if (action && techMap) {
            setCurrentAction(action);
            setCurrentUpdateTechMap(techMap);
            toggleUpdateTechMapModal();
        }
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    if (techMapsLoading || !currentCompanyId) return <Spinner />;

    return (
        <>
            <div className="col-12 table-responsive scrollbar position-relative">
                {!!techMaps?.length ? (
                    <table className="table table-borderless">
                        <thead>
                            <tr className="fs-7">
                                {canEditServiceOnlineBooking && (
                                    <th className="py-2 col-2 ps-4 pe-2 align-middle" style={{ minWidth: "106px" }}>
                                        <div className="d-flex align-items-center gap-2">
                                            <Form.Check
                                                type="checkbox"
                                                ref={parentCheckboxRef}
                                                checked={allChecked}
                                                onChange={handleParentCheckboxChange}
                                            />
                                            <span className="fs-7 text-dark d-flex align-items-center gap-2">
                                                {t("Choose Tech. Maps")}
                                                <TooltipCustom
                                                    placement="bottom"
                                                    text={t(
                                                        "Select technological maps for this service. Products from the selected technological maps will be offered for write-off when the service is performed."
                                                    )}
                                                >
                                                    <i className="bi bi-info-circle fs-7 text-dark d-flex align-items-center d-flex align-items-center"></i>
                                                </TooltipCustom>
                                            </span>
                                        </div>
                                    </th>
                                )}
                                <th className="py-3 col-3 px-2 align-middle line-height-140">
                                    {t("Name of tech map")}
                                </th>
                                <th className="py-3 px-2 col-2 align-middle line-height-140">
                                    {t("ProductsList.Category")}
                                </th>
                                <th className="py-3 px-2 col-5 align-middle line-height-140">{t("Description")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {techMaps.map((techMap) => (
                                <tr key={techMap?.id} className="border-bottom">
                                    {canEditServiceOnlineBooking && (
                                        <td className="align-middle ps-4 py-4 pe-2">
                                            <Form.Check
                                                type="checkbox"
                                                checked={isTechMapConnected(techMap)}
                                                onChange={() => handleTechMapCheckboxChange(techMap)}
                                            />
                                        </td>
                                    )}
                                    <td className="align-middle py-4 px-2">
                                        {canEditServiceTechMaps ? (
                                            <Button
                                                variant="link"
                                                onClick={() => handleOpenEditTechMap(ActionUpdateType.Edit, techMap)}
                                                className={`text-decoration-none text-primary p-0 text-start line-height-140 fs-normal`}
                                            >
                                                {techMap?.name}
                                            </Button>
                                        ) : (
                                            <span className={`text-dark line-height-140`}>{techMap?.name}</span>
                                        )}
                                    </td>
                                    <td className="align-middle py-4 px-2">
                                        <RenderCategoryName category={techMap?.technologicalMapNewCategory} />
                                    </td>
                                    <td className="align-middle py-4 px-2">
                                        <span className="text-dark">{truncateText(techMap?.description, 100)}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : null}
            </div>

            {!!totalItems ? (
                <div className="d-flex justify-content-between align-items-center mt-4 mb-5">
                    <Pagination
                        t={t}
                        pages={Number(pageCount)}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalItems}
                    />
                </div>
            ) : (
                <RenderEmptyList
                    isFilterNotEmpty={isFilterNotEmpty}
                    isEmployeeRole={!canCreateTechMap}
                    visibilityBtn={!isFilterNotEmpty && canCreateTechMap}
                    emptyFilterTitle="No technological maps were found in the filters or search."
                    emptyListTitle="The technological maps have not been created yet. Please add a technological map."
                    onClickButton={() => {
                        if (!canCreateTechMap) return;

                        onShowCreateTechMapModal();
                    }}
                    buttonName={
                        <span>
                            <i className="bi bi-plus-lg fw-bold me-1 w-100"></i>
                            {t("Add Tech.map")}
                        </span>
                    }
                />
            )}

            <UpdateRecoveryTechMap
                show={showUpdateRecoveryTechMapModal}
                onClose={toggleUpdateTechMapModal}
                currentAction={currentAction}
                currentUpdateTechMap={currentUpdateTechMap}
                refetchTechMaps={refetchTechMaps}
                toggleShowToast={toggleShowToast}
                canEditTechMap={canEditServiceTechMaps}
                canCreateTechMapCategory={canCreateTechMapCategory}
            />

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default TechMapsListForServiceTable;
